import React from 'react'
import { Button } from '../buttons'
import capitalize from 'lodash/capitalize'
import { useTranslation } from 'react-i18next'
import { AuthContext, UserInfo } from '../Auth/AuthProvider'
import { OrganizationCategory, Role, WorkingRole } from 'src/gql/graphql'
import { useContext } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useUpdateUserRoleMutation } from 'src/gql/graphql'

interface PlanOverlayModalProps {
  isAdmin: boolean
  message: React.ReactNode
  setClosed: React.Dispatch<React.SetStateAction<boolean>>
  children?: React.ReactNode
}

export const PlanOverlayModal: React.FC<PlanOverlayModalProps> = ({
  isAdmin,
  message,
  children,
  setClosed,
}) => {
  const { t } = useTranslation()
  const history = useHistory()
  const { userInfo, setUserInfo } = useContext(AuthContext)
  const [switchProfile] = useUpdateUserRoleMutation()
  const location = useLocation()

  const excludedRoutes = [
    '/it-dashboard/plan-selection',
    '/it-dashboard/manage-billing',
    '/it-dashboard/payment-details',
    '/payment-success',
    '/payment-failed',
  ]

  const currentRole = userInfo?.employeeDetails?.role?.name
  const employeeId = userInfo?.employeeDetails?.employeeId || ''

  const onClose = () => {
    const { userOrganizations } = userInfo as UserInfo
    const personalOrg = userOrganizations?.find(
      (userOrg) =>
        userOrg.organization.category === OrganizationCategory.Personal,
    )

    if (!personalOrg) return

    const personalOrgId = personalOrg.organization.id
    let newUserRoleId = ''
    localStorage.setItem('organizationId', personalOrgId)

    setUserInfo((prev) => {
      if (!prev) return prev

      const updatedRoles = prev.permissions?.find(
        (permission) => permission?.organizationId === personalOrgId,
      )?.workingRoles as WorkingRole[]

      if (
        updatedRoles.every((role) => role.id !== prev.employeeDetails?.role?.id)
      ) {
        newUserRoleId = updatedRoles[0].id
      }

      return {
        ...prev,
        organization: personalOrg.organization,
        roles: updatedRoles,
      }
    })

    if (newUserRoleId && userInfo?.employeeDetails?.employeeId) {
      switchProfile({
        variables: {
          employeeId: userInfo.employeeDetails.employeeId,
          roleId: newUserRoleId,
        },
        onCompleted: () => {
          window.location.reload()
        },
      })
    }
    setClosed(true)
  }

  const handleSwitchProfile = async () => {
    if (currentRole === Role.It) {
      history.push('/it-dashboard/plan-selection')
      return
    }

    const roleItId = userInfo?.roles?.find((role) => role?.name === Role.It)?.id
    if (!roleItId || !employeeId) return

    await switchProfile({
      variables: {
        employeeId,
        roleId: roleItId,
      },
      onCompleted: () => {
        history.push('/it-dashboard/plan-selection')
        window.location.reload()
      },
    })
  }

  if (excludedRoutes.includes(location.pathname)) {
    return <>{children}</>
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div
        className="px-lg-4 px-md-4 pb-4"
        style={{
          filter: 'blur(3px)',
          pointerEvents: 'none',
          userSelect: 'none',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {children}
      </div>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.4)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: 8,
            padding: '32px 24px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
            minWidth: 280,
            maxWidth: 550,
            width: '100%',
            textAlign: 'center',
            wordBreak: 'break-word',
            position: 'relative',
          }}
        >
          <button
            style={{
              position: 'absolute',
              top: 8,
              right: 8,
              background: 'transparent',
              border: 'none',
              fontSize: 14,
              cursor: 'pointer',
              color: '#888',
            }}
            onClick={onClose}
          >
            ×
          </button>
          <div
            style={{
              marginBottom: 16,
              fontWeight: 400,
              fontSize: 20,
              color: '#555',
              lineHeight: 1.2,
            }}
          >
            {message}
          </div>
          {isAdmin && (
            <Button
              label="Pay"
              style={{
                background: 'var(--primary-color)',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontSize: 22,
                fontWeight: 500,
                padding: '10px 48px',
                margin: '10px 0 0 0',
                boxShadow: '0 2px 8px rgba(148,0,255,0.08)',
                transition: 'background 0.2s',
                cursor: 'pointer',
              }}
              onClick={handleSwitchProfile}
            />
          )}
          {isAdmin && (
            <div style={{ marginTop: 24, color: '#555' }}>
              {capitalize(t('support.contactText'))} :<br />
              <span style={{ fontWeight: 400, fontSize: 16 }}>
                partners@cognentai.com
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
